// Раздел «Списки Bitrix» — реестр универсальных списков и их элементы. ТОЛЬКО ЧТЕНИЕ.
//
// ЧТО ЭТО ЗА ДАННЫЕ. Универсальные списки Bitrix (инфоблоки lists/bitrix_processes):
// верификации, счета БП, отпуска, компетенции — всё, что жило «списками» и не имеет
// своего раздела в CRM. Зеркало переносит элементы с отсечки 01.01.2025; счётчик
// «всего» в реестре — это счёт Bitrix, а «в зеркале» — то, что реально можно открыть.
//
// ДВА БЕЙДЖА РЕЕСТРА, И ОБА — ПОМЕТКИ, А НЕ ФИЛЬТРЫ:
//   «зеркалится отдельно» — у списка уже есть свой синк и свой раздел (счета, командировки);
//     здесь он показан для полноты реестра, работать с ним лучше в своём разделе.
//   «кадровое» — список про людей (компетенции, рабочее время). Прятать его молча
//     значило бы притвориться, что его нет; раздел и так открыт узкой группе.
//
// КОЛОНКИ ТАБЛИЦЫ ПОДБИРАЕТ ЯДРО по заполненности по ВСЕМУ списку, а не по видимой
// странице. Экран их не выбирает и не переставляет: у «Счёта на оплату» полей 91,
// решать, какие шесть из них живые, — вопрос данных, а не вёрстки.
//
// ЗАМАСКИРОВАННЫЕ ЗНАЧЕНИЯ показываются словом «скрыто», а не пустой ячейкой: синк
// намеренно не перенёс значение, и пустое место читалось бы как «ничего не было».
// Пустое поле — прочерк. Это разные вещи, и экран обязан их различать.
//
// Записи отсюда нет: на полях списков в Bitrix висят бизнес-процессы, которые ставят
// задачи живым людям.

import { Fragment, useEffect, useMemo, useState } from 'react'

import {
  useРеестрСписков, useЭлементы, useЭлемент, СписокРеестра, ЭлементСтрока,
  ТИП_СПИСКА,
} from '../api/bxLists'
import { QueryState } from '../components/QueryState'
import { fmtDate } from '../lib/format'

/** Ячейка таблицы/карточки: пусто — прочерк, скрытое — словами. */
function Значение({ text, masked }: { text: string | null; masked: boolean }) {
  if (masked) return <span className="muted">скрыто</span>
  if (text === null || text === '') return <span className="muted">—</span>
  return <>{text}</>
}

// ─────────────────────────── левая колонка: реестр ───────────────────────────

function СтрокаРеестра({ с, выбран, выбрать }: {
  с: СписокРеестра
  выбран: boolean
  выбрать: () => void
}) {
  return (
    <button
      onClick={выбрать}
      className="picker-row"
      aria-pressed={выбран}
      style={{
        display: 'block', width: '100%', textAlign: 'left', cursor: 'pointer',
        border: 'none', background: выбран ? 'var(--c-bg-2, rgba(127,127,127,.15))'
                                           : 'transparent',
        padding: '6px 8px', borderRadius: 6,
      }}
    >
      <div className="picker-row-title">{с.name || `список ${с.iblock_id}`}</div>
      <div className="hint">
        {ТИП_СПИСКА[с.iblock_type] ?? с.iblock_type}
        {' · в зеркале '}{с.elements_since_cut ?? 0}
        {' из '}{с.elements_total ?? 0}
        {' · полей '}{с.fields_total ?? 0}
      </div>
      <div>
        {с.mirrored_elsewhere && (
          <span className="badge work" style={{ marginRight: 4 }}>
            зеркалится отдельно
          </span>
        )}
        {с.kadrovoe && <span className="badge wait">кадровое</span>}
      </div>
    </button>
  )
}

// ─────────────────────────── карточка элемента ───────────────────────────

function КарточкаЭлемента({ iblock, id, закрыть }: {
  iblock: number
  id: number
  закрыть: () => void
}) {
  const к = useЭлемент(iblock, id)
  return (
    <div className="card" style={{ marginTop: 12 }}>
      <div className="page-head" style={{ marginBottom: 8 }}>
        <h3 style={{ margin: 0 }}>
          {к.data?.element.name || `элемент ${id}`}
        </h3>
        <span className="spacer" />
        <button onClick={закрыть}>закрыть</button>
      </div>
      <QueryState isLoading={к.isLoading} error={к.error}>
        {к.data && (
          <>
            <div className="hint">
              {`№ ${к.data.element.id}`}
              {к.data.element.active_from
                ? ` · с ${fmtDate(к.data.element.active_from)}` : ''}
              {(к.data.element.files_count ?? 0) > 0
                ? ` · файлов: ${к.data.element.files_count} (сами файлы не переносились)`
                : ''}
              {(к.data.element.masked_count ?? 0) > 0
                ? ` · скрытых значений: ${к.data.element.masked_count}`
                : ''}
            </div>
            {к.data.fields.length === 0 ? (
              <div className="muted" style={{ marginTop: 8 }}>
                Кроме названия, у элемента не заполнено ни одно поле.
              </div>
            ) : (
              // .dl — грид «подпись | значение»: dt и dd лежат в нём напрямую,
              // обёртка сломала бы колонки
              <dl className="dl" style={{ marginTop: 8 }}>
                {к.data.fields.map((f) => (
                  <Fragment key={f.code}>
                    <dt>{f.name}</dt>
                    <dd>
                      {f.masked && f.values.length === 0
                        ? <Значение text={null} masked />
                        : f.values.length
                          ? f.values.join(' · ')
                          : <Значение text={null} masked={false} />}
                      {f.masked && f.values.length > 0 && (
                        <span className="muted"> · часть значений скрыта</span>
                      )}
                    </dd>
                  </Fragment>
                ))}
              </dl>
            )}
          </>
        )}
      </QueryState>
    </div>
  )
}

// ─────────────────────────── правая колонка: элементы ───────────────────────────

function Элементы({ список }: { список: СписокРеестра }) {
  const [черновик, setЧерновик] = useState('')
  const [q, setQ] = useState('')
  const [start, setStart] = useState(0)
  // Догруженные страницы («показать ещё») копятся здесь: react-query держит по
  // странице на ключ, а человеку нужен один растущий список.
  const [накоплено, setНакоплено] = useState<ЭлементСтрока[]>([])
  const [открыт, setОткрыт] = useState<number | null>(null)

  // Смена списка или поиска — чужие страницы и открытая карточка не её.
  useEffect(() => {
    setStart(0)
    setНакоплено([])
    setОткрыт(null)
  }, [список.iblock_id, q])

  const стр = useЭлементы(список.iblock_id, start, q)

  useEffect(() => {
    if (!стр.data) return
    setНакоплено((были) =>
      стр.data.start === 0 ? стр.data.rows : [...были, ...стр.data.rows])
  }, [стр.data])

  const колонки = стр.data?.columns ?? []
  const строки = накоплено

  return (
    <div>
      <div className="page-head" style={{ marginBottom: 8 }}>
        <h2 style={{ margin: 0 }}>{список.name || `список ${список.iblock_id}`}</h2>
        <span className="spacer" />
        <span className="hint">
          в зеркале {стр.data?.total ?? список.elements_since_cut ?? 0} элементов
          {список.cut_from ? ` (с ${fmtDate(список.cut_from)})` : ''}
        </span>
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); setQ(черновик.trim()) }}
        style={{ display: 'flex', gap: 6, marginBottom: 8 }}
      >
        <input
          value={черновик}
          onChange={(e) => setЧерновик(e.target.value)}
          placeholder="поиск по названию (от 2 знаков)"
          aria-label="поиск по названию"
          style={{ flex: 1 }}
        />
        <button type="submit">Найти</button>
        {q && (
          <button type="button" onClick={() => { setЧерновик(''); setQ('') }}>
            сбросить
          </button>
        )}
      </form>

      <QueryState isLoading={стр.isLoading} error={стр.error}>
        {строки.length === 0 ? (
          <div className="empty">
            <div className="empty-title">
              {q ? 'По такому названию ничего не нашлось' : 'В зеркале нет элементов'}
            </div>
            <div className="empty-hint">
              Зеркало переносит элементы
              с {список.cut_from ? fmtDate(список.cut_from) : 'отсечки'} —
              более старые есть только в Bitrix
              {q ? '; поиск смотрит только на название элемента' : ''}.
            </div>
          </div>
        ) : (
          <div className="card">
            <div className="table-wrap cards">
              <table>
                <thead>
                  <tr>
                    <th>Название</th>
                    <th>Дата</th>
                    {колонки.map((к) => <th key={к.code}>{к.name}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {строки.map((р) => (
                    <tr
                      key={р.id}
                      onClick={() => setОткрыт(р.id)}
                      style={{ cursor: 'pointer' }}
                    >
                      <td data-label="Название">
                        {р.name || <span className="muted">—</span>}
                        {(р.masked_count ?? 0) > 0 && (
                          <div className="hint">скрытых значений: {р.masked_count}</div>
                        )}
                      </td>
                      <td data-label="Дата" className="nowrap">
                        {р.active_from
                          ? fmtDate(р.active_from)
                          : <span className="muted">—</span>}
                      </td>
                      {колонки.map((к) => {
                        const я = р.values[к.code]
                        return (
                          <td key={к.code} data-label={к.name}>
                            <Значение text={я?.text ?? null} masked={!!я?.masked} />
                          </td>
                        )
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {стр.data?.next != null && (
              <div className="pager" style={{ marginTop: 8 }}>
                <button
                  onClick={() => setStart(стр.data!.next!)}
                  disabled={стр.isFetching}
                >
                  {стр.isFetching ? 'грузим…'
                    : `Показать ещё (показано ${строки.length} из ${стр.data.total})`}
                </button>
              </div>
            )}
          </div>
        )}

        {открыт !== null && (
          <КарточкаЭлемента
            iblock={список.iblock_id}
            id={открыт}
            закрыть={() => setОткрыт(null)}
          />
        )}
      </QueryState>
    </div>
  )
}

// ─────────────────────────────── экран ───────────────────────────────

export default function BxLists() {
  const реестр = useРеестрСписков()
  const [выбран, setВыбран] = useState<number | null>(null)

  const списки = реестр.data?.rows ?? []
  const текущий = useMemo(
    () => списки.find((с) => с.iblock_id === выбран) ?? null,
    [списки, выбран],
  )

  return (
    <div>
      <div className="page-head">
        <h1>Списки Bitrix</h1>
        <span className="spacer" />
        <span className="hint">
          только чтение · элементы с 01.01.2025
        </span>
      </div>

      <QueryState isLoading={реестр.isLoading} error={реестр.error}>
        <div
          style={{
            display: 'grid', gap: 12, alignItems: 'start',
            gridTemplateColumns: 'minmax(240px, 320px) minmax(0, 1fr)',
          }}
        >
          <div className="card" style={{ maxHeight: '75vh', overflowY: 'auto' }}>
            <div className="hint" style={{ marginBottom: 6 }}>
              {списки.length
                ? `списков: ${списки.length}`
                : 'реестр пуст — синк списков ещё не отработал'}
            </div>
            {списки.map((с) => (
              <СтрокаРеестра
                key={с.iblock_id}
                с={с}
                выбран={с.iblock_id === выбран}
                выбрать={() => setВыбран(с.iblock_id)}
              />
            ))}
          </div>

          <div>
            {текущий ? (
              <Элементы список={текущий} />
            ) : (
              <div className="empty">
                <div className="empty-title">Выберите список слева</div>
                <div className="empty-hint">
                  Реестр показывает все списки Bitrix: сколько элементов всего и сколько
                  в зеркале (с отсечки). Кадровые списки помечены бейджем, но не
                  спрятаны; списки со своим разделом помечены «зеркалится отдельно».
                </div>
              </div>
            )}
          </div>
        </div>
      </QueryState>
    </div>
  )
}
